// RS meetings widget — adjuster meetings assigned to the viewer this week
// (Ship 2, Aug 25 2026). Fake-UTC columns: naive-string range + raw HH:MM.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { extractAndFormatTime } from "../../../lib/timeUtils";
import { logCardClick } from "../../../lib/uiEvents";

const TERMINAL = [
  "no_thanks",
  "insufficient_damage",
  "claims_denied",
  "closed_won",
  "closed_lost",
  "cancelled",
  "closed_inspection",
];

interface MeetRow {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  when: string | null;
  reinsp: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");
const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function RsMeetingsWidget({ memberId }: { memberId: string }) {
  const [rows, setRows] = useState<MeetRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // This week, Monday → Sunday (local calendar; fake-UTC naive bounds).
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      // Both meeting legs — first adjuster AND reinspection (2nd adjuster) —
      // same pair the home week strip and roleAttention count.
      const wStart = `${isoDate(monday)}T00:00:00Z`;
      const wEnd = `${isoDate(sunday)}T23:59:59Z`;
      const base = () =>
        supabase
          .from("deals")
          .select(
            "id,homeowner_name,property_street,adjuster_meeting_date,reinspection_adjuster_meeting_date",
          )
          .eq("assigned_rs_id", memberId)
          .not("stage", "in", `(${TERMINAL.join(",")})`);
      const [meets, reinsps] = await Promise.all([
        base().gte("adjuster_meeting_date", wStart).lte("adjuster_meeting_date", wEnd),
        base()
          .gte("reinspection_adjuster_meeting_date", wStart)
          .lte("reinspection_adjuster_meeting_date", wEnd),
      ]);
      if (cancelled) return;
      if (!meets.error && !reinsps.error) {
        const out: MeetRow[] = [];
        for (const r of (meets.data as any[]) ?? []) {
          out.push({
            id: r.id,
            homeowner_name: r.homeowner_name,
            property_street: r.property_street,
            when: r.adjuster_meeting_date,
            reinsp: false,
          });
        }
        for (const r of (reinsps.data as any[]) ?? []) {
          out.push({
            id: r.id,
            homeowner_name: r.homeowner_name,
            property_street: r.property_street,
            when: r.reinspection_adjuster_meeting_date,
            reinsp: true,
          });
        }
        out.sort((a, b) => String(a.when ?? "").localeCompare(String(b.when ?? "")));
        setRows(out);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const dayLabel = (ts: string | null) => {
    const iso = String(ts ?? "").slice(0, 10);
    if (!iso) return "";
    const d = new Date(`${iso}T12:00:00Z`);
    return DOW[d.getUTCDay()];
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🏠</span>
        <span className="font-bold text-sm text-bardin-ink">Adjuster Meetings</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {rows ? `${rows.length} this wk` : "…"}
        </span>
      </div>
      {!rows ? (
        <div className="h-9 rounded-lg bg-slate-100 animate-pulse mt-1" />
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">No meetings assigned to you this week.</div>
      ) : (
        <>
          <div className="divide-y divide-slate-100">
            {rows.slice(0, 5).map((r) => (
              <Link
                key={`${r.id}-${r.reinsp ? "r" : "m"}`}
                to={`/deal/${r.id}`}
                onClick={() => logCardClick("widget_rsmeet_row")}
                className="flex items-center gap-2 py-1.5"
              >
                <span className="text-[13px] font-bold text-bardin-ink truncate">
                  {r.homeowner_name ?? "—"}
                </span>
                <span className="text-[11px] text-slate-400 truncate flex-1">
                  {r.property_street ?? ""}
                </span>
                {r.reinsp && (
                  <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-extrabold whitespace-nowrap">
                    2nd adj
                  </span>
                )}
                <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[10px] font-extrabold whitespace-nowrap">
                  {dayLabel(r.when)} {extractAndFormatTime(r.when)}
                </span>
              </Link>
            ))}
          </div>
          {rows.length > 5 && (
            <div className="text-[10px] text-slate-400 font-semibold mt-1">+{rows.length - 5} more</div>
          )}
        </>
      )}
      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/dispatch"
          onClick={() => logCardClick("widget_rsmeet_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Dispatch ›
        </Link>
      </div>
    </div>
  );
}
